import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { Flex, Space, Title } from '@mantine/core';
import { Lock } from '@geist-ui/icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { darkColors, lightColors } from 'src/constants';
import { ColorGroup } from './components';

import styles from './styles.module.scss';

interface Props {
  locked?: boolean;
  onChange: (color: string, isDarkMode: boolean) => void;
  lightMode: string | null;
  darkMode: string | null;
}

export const ColorCustomizer: FC<Props> = ({ locked, onChange, lightMode, darkMode }) => {
  const [t] = useTranslation('widget');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>('');

  const handleModeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setIsDarkMode(e.target.value === 'dark');
    setSelectedColor(e.target.value === 'dark' ? darkColors[0].value : lightColors[0].value);
  };

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSelectedColor(e.target.value);
    onChange(e.target.value, isDarkMode);
  };

  useEffect(() => {
    onChange(selectedColor as string, isDarkMode);
  }, [selectedColor]);

  useEffect(() => {
    if (lightMode) {
      setSelectedColor(lightMode);
      setIsDarkMode(false);
    } else if (darkMode) {
      setSelectedColor(darkMode);
      setIsDarkMode(true);
    }
  }, [lightMode, darkMode]);

  return (
    <Fragment>
      <Flex align="center">
        <Title order={2} fw={'bold'} size={'16px'}>
          {t('colorOption')}
        </Title>
        <Space w={10} />
        {locked && <Lock size={16} aria-label="locked item" />}
      </Flex>
      {!locked && (
        <div className={classNames(styles.colorCustomizer, { dark: isDarkMode, light: !isDarkMode })}>
          <ColorGroup
            colors={lightColors}
            isDarkMode={!isDarkMode}
            handleModeChange={handleModeChange}
            selectedColor={selectedColor}
            handleColorChange={handleColorChange}
            value="light"
            label={t('lightMode')}
          />
          <ColorGroup
            colors={darkColors}
            isDarkMode={isDarkMode}
            handleModeChange={handleModeChange}
            selectedColor={selectedColor}
            handleColorChange={handleColorChange}
            value="dark"
            label={t('darkMode')}
          />
        </div>
      )}
    </Fragment>
  );
};
